import type { FC } from 'react';

import { useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { ButtonLink as SparkyButtonLink } from '@eneco/sparky';
import type { ButtonLinkProps } from '@eneco/sparky/types';

import type { ButtonLinkContent } from './button-link-content.types';

/**
 * `ButtonLink` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ButtonLink/ButtonLink.tsx`: a single Sparky
 * `ButtonLink` whose label, destination and target all come from the one `buttonLink` field, coloured
 * by the `buttonColor` rendering parameter.
 *
 * `buttonColor` normalisation is carried over verbatim from the legacy component — `'primary'` passes
 * through and anything else is lowercased — rather than being tightened to the two values the Sitecore
 * parameters template actually offers (`libs/sitecore/types/manual/ButtonLink.d.ts`), so a stray
 * casing in content behaves identically on both lanes.
 *
 * Drops the legacy `useLinkComponent()` in favour of `linkWithUrl()` plus Sparky's own `href`,
 * matching every other migrated component in this lane (see `LinkCard.tsx`, `HeroCard.tsx`).
 *
 * A link whose URL could not be built leaves the block empty: the link is its entire payload, and no
 * button is better than one pointing at the wrong place.
 */
const normalizeButtonColor = (color: string): ButtonLinkProps['action'] =>
  color === 'primary' ? 'primary' : (color.toLowerCase() as ButtonLinkProps['action']);

const ButtonLink: FC = () => {
  const content = useContent<ButtonLinkContent>();
  const buttonColor = useSetting('buttonColor');

  const link = linkWithUrl(content.buttonLink);
  if (!link?.href) return null;

  const action = buttonColor ? normalizeButtonColor(buttonColor) : undefined;

  return (
    <SparkyButtonLink href={link.href} action={action} target={link.target}>
      {link.text}
    </SparkyButtonLink>
  );
};

export default ButtonLink;
